import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { EupRoutesService, RoutingData } from '../../core/eupRoutes.service';
import { Observable, BehaviorSubject, throwError, of, forkJoin, EMPTY, from } from 'rxjs';
import { catchError, map, switchMap, tap, take } from 'rxjs/operators';
import { GlobalSettings, GlobalSettingsService } from '../../core/globalSettings.service';
import { EupHttpHandler } from '../../core/eupHttpHandler.service';
import { ContactWithBusinessPartners } from '../../shared/generalInterfaces';
import { Params, Router } from '@angular/router';
import { AuthenticationStatus } from './models/authentication-status';
import { IOSimSimulationInfoStore } from '../iosim-simulation.store/iosim-simulation-status-progress.store';
import { Consts } from '@shared/consts';
import { DownloadNotificationService } from '@shared/downloadNotification/downloadNotification.service';
import { LogoutParameters } from './models/logout-parameters';
import { BusinessPartnerType, FeatureToggle, SoftwareOptionsForCompany, RoleTypeEnum } from '@shared/enums';
import { SpinnerService } from '@core/spinner/spinner.service';
import { ShellContextService } from '../shell-context/shell-context.service';
import { CommunicationService } from '../client-communication/communication.service';
import { AppUpdater } from '@shared/appUpdater.service';
import { StickyHeaderService } from '../stickyHeaderService/stickyHeader.service';
import { PendoService } from '@shared/pendo/pendo.service';
import { FeatureToggleSettings } from 'app/featuresToggleSettings/featuresToggleSettings.model';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { TimberService } from '@logging/timber.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { LabService } from 'app/labs/lab.service';
import { AuthProvider, CompanyType, CookieAuthService, CookieName } from '@nodeModules/@itero/auth-angular';
import { UserCompanyDataResponse } from 'app/models/data.model';
import { ClientDiscoveryService } from 'app/platform/services/client-discovery/client-discovery.service';
import { KnownComponentsNames } from '@core/components-names';
import { CookieService } from '@core/cookie.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private appId = 'midc';
	private invalidateSessionUrl = (sessionId: string) => `${this.eupRoutesService.iTeroWebAuthApiUrl}session/invalidate?sessionId=${sessionId}&appId=${this.appId}`;

	authStatus$: BehaviorSubject<AuthenticationStatus>;

	constructor(private http: EupHttpHandler,
				private eupRoutesService: EupRoutesService,
				private globalSettingsService: GlobalSettingsService,
				private router: Router,
				private simulationStatusProgressStore: IOSimSimulationInfoStore,
				private downloadNotificationService: DownloadNotificationService,
				private spinnerService: SpinnerService,
				private shellContextService: ShellContextService,
				private communicationService: CommunicationService,
				private appUpdater: AppUpdater,
				private applicationNavigationService: ApplicationNavigationService,
				private labService: LabService,
				private stickyHeaderService: StickyHeaderService,
				private pendoService: PendoService,
				private featuresToggleSettingsService: FeaturesToggleSettingsService,
				private softwareOptionsService: SoftwareOptionsService,
				private timberService: TimberService,
				private cookieAuthService: CookieAuthService,
				private cookieService: CookieService,
	) {
		this.authStatus$ = new BehaviorSubject<AuthenticationStatus>({ isAuthenticated: this.isAuthenticated() });
	}

	startSession(credentials: UserCredentials): Observable<any> {
		const startSessionUrl = `${this.eupRoutesService.iTeroWebAuthApiUrl}session/start?appId=${this.appId}`;
		let headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});
		const startSession$ = this.http.post(startSessionUrl, { username: credentials.username, password: credentials.password }, { headers, observe: 'response' });
		const auth = () => startSession$.pipe(
			map(res => res.body),
			catchError(err => this.throwError(err)));

		return this.eupRoutesService.doAfterInit(auth);
	}

	proceedLoginFlow(sessionInfo: SessionInfo, credentials: UserCredentials, authProvider: AuthProvider): Observable<GlobalSettings | any> {
		let headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		localStorage.setItem(
			Consts.Storage.SessionInfo,
			JSON.stringify({
				accessToken: sessionInfo.accessToken,
				sessionId: sessionInfo.sessionId,
				sessionType: Consts.SessionType,
				idToken: sessionInfo.idToken,
			} as SessionInfo)
		);
		headers = headers.set(Consts.HeaderKeys.Authorization, `Bearer ${sessionInfo.accessToken}`);

		const login$ = this.prepareLoginObservable(credentials, sessionInfo, headers);
		const getUserData$ = this.prepareGetUserDataObservable(credentials, headers);
		const getUserCompanyData$ = this.prepareGetUserCompanyDataObservable(credentials, headers);

		return login$.pipe(
			switchMap(() => this.featuresToggleSettingsService.getFeaturesToggleSettings()),
			map(() =>
				this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.EnablePlatformSelector, false)
			),
			switchMap((isFeatureEnabled) => isFeatureEnabled
				? this.handleFeatureEnabled(getUserCompanyData$, getUserData$, sessionInfo, authProvider)
				: this.handleUserLogin(getUserData$, sessionInfo)
			),
			take(1),
			catchError((error) => {
				if (sessionInfo.sessionId.trim() !== '') {
					return this.invalidateSession(sessionInfo.sessionId).pipe(switchMap(() => this.throwError(error)));
				}
				this.throwError(error);
			})
		);
	}

	private prepareLoginObservable(credentials: UserCredentials, sessionInfo: SessionInfo, headers: HttpHeaders): Observable<any> {
		return this.http.post(
			this.eupRoutesService.login.loginToMidc,
			{ username: credentials.username, password: credentials.password, sessionId: sessionInfo.sessionId },
			{ headers, observe: 'response' }
		);
	}
	
	private prepareGetUserDataObservable(credentials: UserCredentials, headers: HttpHeaders): Observable<any> {
		return this.http.get<{ body: GlobalSettings }>(
			this.eupRoutesService.login.getUserData(credentials.username),
			{ headers, observe: 'response' }
		);
	}
	
	private prepareGetUserCompanyDataObservable(credentials: UserCredentials, headers: HttpHeaders): Observable<any> {
		return this.http.get<{ body: UserCompanyDataResponse }>(
			this.eupRoutesService.login.getUserCompanyData(credentials.username),
			{ headers, observe: 'response' }
		);
	}

	private handleFeatureEnabled(
		getUserCompanyData$: Observable<any>,
		getUserData$: Observable<any>,
		sessionInfo: SessionInfo,
		authProvider: AuthProvider
	): Observable<any> {
		return getUserCompanyData$.pipe(
			map((data) => data.body),
			tap((data: UserCompanyDataResponse) => {
				this.softwareOptionsService.updateCompanySoftwareOptions(data.companySoftwareOptions, new Date());
			}),
			switchMap((data) =>
				from(this.handleCompanyTypes(data, getUserData$, sessionInfo, authProvider)).pipe(
				  switchMap((result$) => result$)
				)
			)
		);
	}

	private async handleCompanyTypes(
		data: UserCompanyDataResponse,
		getUserData$: Observable<any>,
		sessionInfo: SessionInfo,
		authProvider: AuthProvider
	): Promise<Observable<any>> {
		const companyType = data.companiesTypes[0]?.businessPartnerTypeId;
		const companyId = data.companiesTypes[0]?.businessPartnerId;
		const cswo = data.companySoftwareOptions;
		switch (companyType) {
			case BusinessPartnerType.PartnerThirdPartySW:
				this.setCookies(sessionInfo, CompanyType.Partner, authProvider, companyId);
				const entApp = await ClientDiscoveryService.getClientApplication(KnownComponentsNames.MyiteroEnt);
				window.location.href = entApp?.url;
				return EMPTY;
			case BusinessPartnerType.Lab:
			case BusinessPartnerType.Externallab:
			case BusinessPartnerType.LabOnline:
			case BusinessPartnerType.LabType:
				if (!Object.entries(cswo).some(([key]) => cswo[key] === SoftwareOptionsForCompany.cswo_UseMiflPlatform)) {
					return this.handleUserLogin(getUserData$, sessionInfo);
				} else {
					this.setCookies(sessionInfo, CompanyType.Lab, authProvider, companyId);
					const labApp = await ClientDiscoveryService.getClientApplication(KnownComponentsNames.MyiteroLab);
					window.location.href = labApp?.url;
					return EMPTY;
				}
			case BusinessPartnerType.Clinic:
			default:
				return this.handleUserLogin(getUserData$, sessionInfo);
		}
	}	

	private handleUserLogin(getUserData$: Observable<{body: GlobalSettings}>, sessionInfo: SessionInfo): Observable<any> {
		return getUserData$.pipe(
			map((data) => data.body),
			tap((data) => this.setUserLogin(data, sessionInfo))
		);
	}

	setUserLogin(data, sessionInfo: SessionInfo) {
		this.communicationService.setSessionInfo(sessionInfo.sessionId);
		if (data.companies && data.companies[0]?.region) {
			this.communicationService.setRegion(data.companies[0].region);
		}

		this.shellContextService.updateContext((ctx) => {
			ctx.session.sessionId = sessionInfo.sessionId;
			ctx.security.accessToken = sessionInfo.accessToken;
			ctx.user.loggedInUserId = data.contactId;
			ctx.user.roleType = data.roleType;
			ctx.session.sessionType = Consts.SessionType;

			if (data.dateFormat || data.selectedLanguage?.code) {
				ctx.UI.dateFormat = data.dateFormat;
				ctx.UI.language = data.selectedLanguage?.code || 'en-US';
			}
		});
	}

	impersonate(user: ImpersonateUserCredentials): Observable<GlobalSettings> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});

		const auth = () => this.http.post(
			this.eupRoutesService.logonAs.url,
			{ contactId: user.contactId, businessPartnerId: user.businessPartnerId, sessionId: user.sessionId },
			{ headers, observe: 'response' }
		)
		.pipe(
			map((res: any) => {
				if (res.hasOwnProperty('body') && Object.keys(res.body).length > 0) {
					this.authStatus$.next({ isAuthenticated: true });
					return res.body as GlobalSettings;
				} else {
					// in case we want to redirect the user to another route, we need to get an error in order to make unsubscribe the login subscription
					return res.json();
				}
			})
		);

		return this.eupRoutesService.doAfterInit(auth);
	}

	getContactWithBusinessPartners(userName: string): Observable<ContactWithBusinessPartners[]> {
		const params = new HttpParams().set('contactName', userName);

		return this.http.get(this.eupRoutesService.logonAs.getContacts, { params: params }, null, false);
	}

	getContactWithBusinessPartnersById(contactId: number): Observable<ContactWithBusinessPartners[]> {
		const params = new HttpParams().set('contactId', contactId);

		return this.http.get(this.eupRoutesService.logonAs.getContactsById, { params: params }, null, false);
	}

	getDetails(): Observable<LoginDetails> {
		const getLogin = () => this.http.get(this.eupRoutesService.getLoginData(), undefined, undefined, false);
		return this.eupRoutesService.doAfterInit(getLogin, true);
	}

	isAuthenticated(): boolean {
		const settings = this.globalSettingsService.get();
		return !!settings;
	}

	async logoutClient(params: LogoutParameters = {}): Promise<void> {
		this.timberService.info(`logout client called. logoutUrl: ${params.logoutUrl }`, { module: 'AuthService' });

		try {
			const queryParams: Params = { returnUrl: params.returnUrl };
			const userId = this.globalSettingsService.get()?.contactId;
			this.globalSettingsService.clear();
			await this.resetCache();
			this.authStatus$.next(
				{ 
				isAuthenticated: false,
				isUnauthenticatedByTimeout: params.byTimeout,
				userId
				});
			this.downloadNotificationService.clear();
			this.simulationStatusProgressStore.reset();
			this.spinnerService.stop();
			if (params.logoutUrl  && params.logoutUrl.trim() !== '') {
				this.timberService.info(`logout client. redirect to logoutUrl: ${params.logoutUrl }`, { module: 'AuthService' });
							
			if (params.returnUrl) {
				this.cookieService.set(Consts.CookiesNames.RETURNURL, params.returnUrl, Number.MAX_SAFE_INTEGER);
			}
				
				window.location.href = params.logoutUrl;	
			} else {		
			this.cookieService.set(Consts.CookiesNames.RETURNURL, '', Number.MAX_SAFE_INTEGER);
			this.router.navigate(['/login'], { queryParams });
			}
		}
		finally
		{
		  this.appUpdater.checkForUpdates();
		}
	}

	logout(sessionId: string = '', params: LogoutParameters = {}): Observable<any> {
		const featureFlags = [FeatureToggle.IMSUserFlow, FeatureToggle.Auth0IsEnabled];
	
		return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
			take(1),
			map((res: FeatureToggleSettings[]) => featureFlags.every(f => res.some(ff => ff.id === f && ff.isActive))),
			switchMap((flagsOn) => flagsOn ? this.logoutNewFlow(sessionId, params) : this.logoutLegacy(sessionId, params))
		);
	}

	logoutNewFlow(sessionId: string = '', params: LogoutParameters = {}): Observable<any> {
		this.timberService.info(`logout new flow called. sessionId: ${sessionId}`, { module: 'AuthService' });
	
		const sessionInfoFromStorage = localStorage.getItem(Consts.Storage.SessionInfo);
		const sessionInfo = sessionInfoFromStorage ? JSON.parse(sessionInfoFromStorage) as SessionInfo : undefined;
	  
		if (!sessionInfo) {
		  this.timberService.error('No session info found in storage', { module: 'AuthService' });
		  return of({});
		}
	  
		const redirectUrl = `${window.location.origin.replace(/\/$/, '')}/login`;
		this.timberService.info(`logout new flow. redirectUrl: ${redirectUrl}`, { module: 'AuthService' });
	
		const url = `${this.eupRoutesService.iTeroWebAuthApiUrl.replace(/\/auth\/?$/, '')}/${Consts.LogoutPath}`;
		const body = {
		  sessionId: sessionInfo.sessionId,
		  appId: this.appId,
		  redirectUri: redirectUrl,
		  idToken: sessionInfo.idToken
		};
	
		this.timberService.info(`logout new flow. get logout url params: ${JSON.stringify(body)}`, { module: 'AuthService' });
	
		return this.http.post(url, body).pipe(
		  switchMap(res => {
			this.timberService.info(`logout new flow. logout res: ${JSON.stringify(res)} params: ${JSON.stringify(params)}`, { module: 'AuthService' });	  
			if (res.logoutUrl) {
			  params.logoutUrl = res.logoutUrl;
			}	  
			this.timberService.info(`logout new flow. updated params: ${JSON.stringify(params)}`, { module: 'AuthService' });	  
			const invalidate$ =
			sessionInfo && sessionInfo.sessionId.trim() !== '' &&
				(!sessionId || (sessionId && sessionInfo.sessionId.trim() === sessionId.trim())) ?
				this.invalidateSession(sessionInfo.sessionId) : of({});
	  
			this.timberService.info('logout new flow. invalidate', { module: 'AuthService' });
			return invalidate$.pipe(
			  catchError((error) => {
				this.timberService.error('Error in calling invalidate', { module: 'AuthService', error: error });
				return of({});
			  }),
			  switchMap(async _ => {
				await this.logoutClient(params);
				return of({});
			  })
			);
		  }),
		  catchError((error) => {
			this.timberService.error('Error in logoutNewFlow', { module: 'AuthService', error: error });
			return of({});
		  })
		);
	  }

	logoutLegacy(sessionId: string = '', params: LogoutParameters = {}): Observable<any> {
		const sessionInfoFromStorage = localStorage.getItem(Consts.Storage.SessionInfo);
		const sessionInfo = sessionInfoFromStorage ? JSON.parse(sessionInfoFromStorage) as SessionInfo : undefined;
		const logoutUrl = this.globalSettingsService.get()?.roleType === RoleTypeEnum.Lab ? this.eupRoutesService.lab.logout : this.eupRoutesService.home.logout;

		const invalidate$ = 
			sessionInfo && sessionInfo.sessionId.trim() !== '' && 
			(!sessionId || (sessionId && sessionInfo.sessionId.trim() === sessionId.trim())) ?
				this.invalidateSession(sessionInfo.sessionId) : of({});

		return forkJoin([this.http.postWithoutCancel(logoutUrl).pipe(catchError(() => {
			return of({});
		})), invalidate$.pipe(catchError(() => {
			return of({});
		}))]).pipe(
			map(async _ => await this.logoutClient(params))
		);
    }
	
	invalidateSession(sessionId: string): Observable<boolean> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});
		return this.http
			.deleteWithoutCancel(this.invalidateSessionUrl(sessionId), { headers, observe: 'response' })
			.pipe(
				map((res: any) => {
					return res.body;
				}),
				catchError(error => {
					return of(false);
				})
			);
	}

	private async resetCache(): Promise<void> {
		this.shellContextService.resetContext();
		this.stickyHeaderService.resetLogo();
		this.pendoService.closePendoInstance();
		await this.featuresToggleSettingsService.resetFeaturesToggles();
		this.softwareOptionsService.resetCompanySoftwareOptions();
		this.applicationNavigationService.reset();
		this.labService.reset();
		this.clearCookies();
	}

	private throwError(error: any)  {
		const newError = new Error(error.message);
		newError['status'] = error.status;
		return throwError(() => newError);
	}

	private setCookies(sessionInfo: SessionInfo, companyType: CompanyType, authProvider: AuthProvider, companyId: number) {
		const routingData = JSON.parse(localStorage.getItem(Consts.Storage.Routes)!) as RoutingData;
		const region = routingData[Consts.Storage.Region];
		this.cookieAuthService.setCookie(CookieName.COMPANYID, companyId);
		this.cookieAuthService.setCookie(CookieName.COMPANYTYPES, companyType);
		this.cookieAuthService.setCookie(CookieName.REGION, region);
		this.cookieAuthService.setCookie(CookieName.IDENTITYPROVIDER, authProvider);
		this.cookieAuthService.setCookie(CookieName.SESSIONID, sessionInfo.sessionId);
		this.cookieAuthService.setCookie(CookieName.ITEROTOKEN, sessionInfo.accessToken);
		this.cookieAuthService.setCookie(CookieName.IDTOKEN, sessionInfo.idToken);
	}

	private clearCookies() {
		this.cookieAuthService.removeCookie(CookieName.COMPANYID);
		this.cookieAuthService.removeCookie(CookieName.COMPANYTYPES);
		this.cookieAuthService.removeCookie(CookieName.REGION);
		this.cookieAuthService.removeCookie(CookieName.IDENTITYPROVIDER);
		this.cookieAuthService.removeCookie(CookieName.SESSIONID);
		this.cookieAuthService.removeCookie(CookieName.ITEROTOKEN);
		this.cookieAuthService.removeCookie(CookieName.IDTOKEN);
	}
}

export class UserCredentials {
	username: string;
	password: string;

	constructor(username: string, password: string) {
		this.username = username;
		this.password = password;
	}
}

export interface SessionInfo {
	sessionId: string;
	accessToken: string;
	sessionType: string;
	idToken: string;
}

export class ImpersonateUserCredentials {
	contactId: number;
	businessPartnerId: number;
	sessionId?: string;

	constructor(contactId: number, businessPartnerId: number, sessionId?: string) {
		this.businessPartnerId = businessPartnerId;
		this.contactId = contactId;
		this.sessionId = sessionId;
	}
}

export class LoginDetails {
	appVersion: string;
	bffVersion: string;
	databaseVersion: string;
	forgotPasswordUrl: string;
	dbInfo: string;
	region: string;
	regionsEndpoints: { [key: string]: string };
}
